import axios from "axios";

const BASE = process.env.REACT_APP_BACKEND_URL;
const api = axios.create({ baseURL: `${BASE}/api`, withCredentials: true });

// Robust cross-domain auth: attach bearer token (fallback when 3rd-party cookies are blocked)
api.interceptors.request.use((config) => {
  try {
    const tok = localStorage.getItem("vs_token");
    if (tok) config.headers.Authorization = `Bearer ${tok}`;
  } catch { /* noop */ }
  return config;
});

function storeToken(data) {
  try { if (data && data.token) localStorage.setItem("vs_token", data.token); } catch { /* noop */ }
  return data;
}

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Si è verificato un errore. Riprova.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

// ----- Auth -----
export async function registerUser(payload) {
  const { data } = await api.post("/auth/register", payload);
  return storeToken(data);
}
export async function loginUser(payload) {
  const { data } = await api.post("/auth/login", payload);
  return storeToken(data);
}
export async function createOAuthState() {
  const { data } = await api.post("/auth/oauth-state", {});
  return data.state;
}
export async function googleSession(session_id, state) {
  const { data } = await api.post("/auth/session", { session_id, state });
  return storeToken(data);
}
export async function logoutUser() {
  try { localStorage.removeItem("vs_token"); } catch { /* noop */ }
  await api.post("/auth/logout");
}
export async function getMe() {
  const { data } = await api.get("/auth/me");
  return data;
}

// ----- License -----
export async function getLicense() {
  const { data } = await api.get("/license");
  return data;
}
export async function saveLicenseConfig(config) {
  const { data } = await api.post("/license/config", config);
  return data;
}

// ----- Reports -----
// Consuma il credito gratuito e restituisce logo/firma: nessun dato clinico lascia il
// browser, il body contiene solo l'identificativo del referto e la lingua.
export async function authorizeReport(reportId, lang) {
  const { data } = await api.post("/reports/authorize", { report_id: reportId, lang });
  return data;
}

// ----- Payments -----
export async function createCheckout() {
  const { data } = await api.post("/payments/checkout", { origin_url: window.location.origin });
  return data;
}
export async function getPaymentStatus(sessionId) {
  const { data } = await api.get(`/payments/status/${sessionId}`);
  return data;
}

// ----- Admin -----
export async function getAdminUsers() {
  const { data } = await api.get("/admin/users");
  return data;
}
export async function adminUnlock(userId) {
  const { data } = await api.post(`/admin/users/${userId}/unlock`);
  return data;
}
export async function adminRevoke(userId) {
  const { data } = await api.post(`/admin/users/${userId}/revoke`);
  return data;
}
export async function adminSetActive(userId, active) {
  const { data } = await api.post(`/admin/users/${userId}/active`, { active });
  return data;
}
export async function adminSetFreeCredits(userId, limit) {
  const { data } = await api.post(`/admin/users/${userId}/free-credits`, { limit });
  return data;
}
export async function adminCreateUser(payload) {
  const { data } = await api.post("/admin/users", payload);
  return data;
}
// L'email va nel corpo, non nella query: il server la confronta con quella dell'account prima
// di cancellare, così una riga stale nel pannello non elimina la persona sbagliata. Su DELETE
// axios vuole il corpo dentro `data` della config, non come secondo argomento.
export async function adminDeleteUser(userId, email) {
  const { data } = await api.delete(`/admin/users/${userId}`, { data: { email } });
  return data;
}

// ----- Password reset -----
export async function forgotPassword(email, lang) {
  const { data } = await api.post("/auth/forgot-password", { email, origin_url: window.location.origin, lang });
  return data;
}
export async function resetPassword(token, password) {
  const { data } = await api.post("/auth/reset-password", { token, password });
  return data;
}
